import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'

import {
    Button,
    Divider,
    FormControlLabel,
    IconButton,
    Switch,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'

import { Container } from '../../../components'
import {
    AreaConfig,
    AreaOrder,
    AreaType,
    convertListToMap,
    IdBoolMap,
    InspectionType,
    InventoryConfig,
    StatusGroup,
} from '../../../models'
import { CONGIFURE_INSPECTION_TYPE_STEP } from './InspectionConfigurationModal'

const sortAreaConfigsByOrder = (
    areaConfigs: AreaConfig[],
    areaOrder: AreaOrder,
): AreaConfig[] => {
    const sorted = [...areaConfigs]
    switch (areaOrder) {
        case AreaOrder.BY_CREATION:
            return sorted
        case AreaOrder.ALPHABETICAL:
            return sorted.sort((a, b) => {
                const aName = `${a.name} ${
                    a.unit_area_config?.area_label ?? ''
                }`
                const bName = `${b.name} ${
                    b.unit_area_config?.area_label ?? ''
                }`
                return aName.localeCompare(bName)
            })
        case AreaOrder.COM_BED_BATH_COMBO:
            return sorted.sort((areaA, areaB) => {
                const aName = `${areaA.name} ${
                    areaA.unit_area_config?.area_label ?? ''
                }`
                const bName = `${areaB.name} ${
                    areaB.unit_area_config?.area_label ?? ''
                }`

                const aIsCom = areaA.area_type === AreaType.COM
                const bIsCom = areaB.area_type === AreaType.COM
                const aIsBed = areaA.area_type === AreaType.BED
                const bIsBed = areaB.area_type === AreaType.BED
                const aIsBath = areaA.area_type === AreaType.BATH
                const bIsBath = areaB.area_type === AreaType.BATH

                // COMs come first
                if (aIsCom && !bIsCom) return -1
                if (!aIsCom && bIsCom) return 1

                // If both are COMs, sort alphabetically by full name
                if (aIsCom && bIsCom) {
                    return aName.localeCompare(bName)
                }

                // Both are Bed/Bath - group by label, then Bed before Bath within same label
                const aLabel = areaA.unit_area_config?.area_label ?? ''
                const bLabel = areaB.unit_area_config?.area_label ?? ''
                const labelCompare = aLabel.localeCompare(bLabel)
                if (labelCompare !== 0) {
                    return labelCompare
                }

                // Same label - Bed comes before Bath
                if (aIsBed && bIsBath) return -1
                if (aIsBath && bIsBed) return 1

                // Same type and label, sort by config name
                return areaA.name.localeCompare(areaB.name)
            })
    }
}
import { updateInventoryConfigReq } from '../../../store'
import { useAppDispatch } from '../../../hooks'
import { AreaConfigButton } from './AreaConfigButton'
import { InventoryConfigRow } from './InventoryConfigRow'

interface Props {
    invItemsMap: IdBoolMap
    inventoryConfigList: InventoryConfig[]
    areaConfigList: AreaConfig[]
    statusGroupList: StatusGroup[]
    hasEditPermission: boolean
    inspectionType?: InspectionType
    areaOrder: AreaOrder
    handleClose: () => void
    setTab: (tab: number) => void
    setInvItemsMap: (invItemsMap: IdBoolMap) => void
    inventoryOrder: number[]
    setInventoryOrder: (order: number[]) => void
    saveInspectionType: () => void
}

export const PreviewInspectionType = (props: Props) => {
    const {
        invItemsMap,
        inspectionType,
        inventoryConfigList,
        areaConfigList,
        statusGroupList,
        hasEditPermission,
        areaOrder,
        handleClose,
        setTab,
        setInvItemsMap,
        inventoryOrder,
        setInventoryOrder,
        saveInspectionType,
    } = props

    const theme = useTheme()

    const dispatch = useAppDispatch()

    const inventoryConfigMap = useMemo(() => {
        return convertListToMap(inventoryConfigList)
    }, [inventoryConfigList])

    const [
        selectedAreaConfig,
        setSelectedAreaConfig,
    ] = useState<AreaConfig | null>(null)
    const [showAllInventory, setShowAllInventory] = useState(false)

    const selectedAreaConfigs = useMemo(() => {
        const filtered = areaConfigList.filter((areaConfig) =>
            areaConfig.inventory_area_configs?.some(
                (inventoryAreaConfig) =>
                    invItemsMap[inventoryAreaConfig.inventory_config.id],
            ),
        )
        return sortAreaConfigsByOrder(filtered, areaOrder)
    }, [areaConfigList, invItemsMap, areaOrder])

    // Helper function to sort inventory by custom order
    const sortInventoryByOrder = useCallback(
        (inventoryItems: InventoryConfig[]): InventoryConfig[] => {
            if (inventoryOrder.length > 0) {
                const ordered: InventoryConfig[] = []
                const unordered: InventoryConfig[] = []

                // First, add items in the custom order (include every
                // instance so duplicates from amount > 1 are preserved)
                inventoryOrder.forEach((id) => {
                    const items = inventoryItems.filter((inv) => inv.id === id)
                    ordered.push(...items)
                })

                // Then add any items not in the order
                inventoryItems.forEach((inv) => {
                    if (!inventoryOrder.includes(inv.id)) {
                        unordered.push(inv)
                    }
                })

                return [...ordered, ...unordered]
            }

            return inventoryItems
        },
        [inventoryOrder],
    )

    // Get all selected inventory items
    const allSelectedInventory = useMemo(() => {
        const selected = inventoryConfigList.filter(
            (invConfig) => invItemsMap[invConfig.id],
        )
        return sortInventoryByOrder(selected)
    }, [inventoryConfigList, invItemsMap, sortInventoryByOrder])

    // Get sorted inventory items for the selected area
    const selectedAreaInventory = useMemo(() => {
        if (!selectedAreaConfig) return []

        const areaInventory: InventoryConfig[] = []
        selectedAreaConfig.inventory_area_configs?.forEach(
            (inventoryAreaConfig) => {
                const inventoryConfig =
                    inventoryConfigMap[inventoryAreaConfig.inventory_config.id]
                // Always use the up-to-date config from inventoryConfigMap so
                // edits (e.g. status updates) are reflected on every instance,
                // including duplicated rows from amount > 1.
                if (inventoryConfig && invItemsMap[inventoryConfig.id]) {
                    if (inventoryAreaConfig.amount > 1) {
                        for (let i = 0; i < inventoryAreaConfig.amount; i++) {
                            areaInventory.push(inventoryConfig)
                        }
                    } else {
                        areaInventory.push(inventoryConfig)
                    }
                }
            },
        )
        return sortInventoryByOrder(areaInventory)
    }, [
        selectedAreaConfig,
        inventoryConfigList,
        inventoryConfigMap,
        invItemsMap,
        sortInventoryByOrder,
    ])

    // Automatically select the first selectedAreaConfig only when current selection is invalid
    useEffect(() => {
        if (selectedAreaConfigs.length > 0 && !showAllInventory) {
            const currentStillValid =
                selectedAreaConfig &&
                selectedAreaConfigs.some((a) => a.id === selectedAreaConfig.id)
            if (!currentStillValid) {
                setSelectedAreaConfig(selectedAreaConfigs[0])
            }
        }
    }, [selectedAreaConfigs, showAllInventory, selectedAreaConfig])

    // Initialize inventory order if empty
    useEffect(() => {
        if (inventoryOrder.length === 0 && allSelectedInventory.length > 0) {
            setInventoryOrder(
                allSelectedInventory.map((inv: InventoryConfig) => inv.id),
            )
        }
    }, [allSelectedInventory, inventoryOrder.length, setInventoryOrder])

    const handleDragEnd = (result: any) => {
        if (!result.destination) {
            return
        }

        const items = Array.from(allSelectedInventory)
        const [reorderedItem] = items.splice(result.source.index, 1)
        items.splice(result.destination.index, 0, reorderedItem)

        setInventoryOrder(items.map((inv: InventoryConfig) => inv.id))
    }

    const updateInventoryConfig = (
        inventoryConfig: InventoryConfig,
        keyValuePairs: { key: string; value: string }[],
    ) => {
        const invConfigFormData = new FormData()
        keyValuePairs.forEach(({ key, value }) => {
            invConfigFormData.append(key, value)
        })
        dispatch(
            updateInventoryConfigReq({
                id: inventoryConfig.id,
                req: invConfigFormData,
            }),
        ).then(() => {
            toast.success(`${inventoryConfig.name} updated successfully!`)
        })
    }

    return (
        <Container style={{ flex: 1, flexDirection: 'column' }}>
            <Container
                alignItems="center"
                style={{ marginBottom: theme.spacing(2) }}
            >
                <Container
                    style={{
                        fontWeight: 600,
                        fontSize: '20px',
                        flex: 1,
                    }}
                >
                    Step 2: Preview Inspection
                </Container>
                <Tooltip title="Exit">
                    <IconButton
                        onClick={() => {
                            handleClose()
                        }}
                    >
                        <CloseIcon
                            fontSize="inherit"
                            style={{
                                color: theme.palette.darkGreen.main,
                            }}
                        />
                    </IconButton>
                </Tooltip>
            </Container>

            <Divider />

            <Container
                style={{
                    marginTop: theme.spacing(2),
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                {!showAllInventory && (
                    <Container
                        style={{
                            paddingBottom: theme.spacing(1),
                            overflowX: 'auto',
                            display: 'flex',
                            flex: 1,
                        }}
                    >
                        {selectedAreaConfigs.map((areaConfig) => (
                            <AreaConfigButton
                                key={areaConfig.id}
                                areaConfig={areaConfig}
                                selected={
                                    selectedAreaConfig?.id === areaConfig.id
                                }
                                theme={theme}
                                onClick={() => {
                                    setSelectedAreaConfig(areaConfig)
                                }}
                            />
                        ))}
                    </Container>
                )}
                {showAllInventory && <div style={{ flex: 1 }} />}
                <FormControlLabel
                    control={
                        <Switch
                            checked={showAllInventory}
                            onChange={(e) => {
                                setShowAllInventory(e.target.checked)
                                if (
                                    !e.target.checked &&
                                    selectedAreaConfigs.length > 0
                                ) {
                                    setSelectedAreaConfig(
                                        selectedAreaConfigs[0],
                                    )
                                }
                            }}
                            color="primary"
                        />
                    }
                    label="Show All Inventory (Adjust Order)"
                    style={{ marginLeft: theme.spacing(2) }}
                />
            </Container>

            <Container
                style={{
                    marginTop: theme.spacing(2),
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: 'calc(100vh - 325px)',
                    minHeight: 'calc(100vh - 325px)',
                }}
            >
                {showAllInventory ? (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="inventory-list">
                            {(provided) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    style={{ flex: 1 }}
                                >
                                    {allSelectedInventory.map(
                                        (
                                            inventoryConfig: InventoryConfig,
                                            index: number,
                                        ) => (
                                            <Draggable
                                                key={inventoryConfig.id}
                                                draggableId={String(
                                                    inventoryConfig.id,
                                                )}
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        style={{
                                                            ...provided
                                                                .draggableProps
                                                                .style,
                                                            marginBottom: theme.spacing(
                                                                1,
                                                            ),
                                                            opacity: snapshot.isDragging
                                                                ? 0.8
                                                                : 1,
                                                        }}
                                                    >
                                                        <Container
                                                            style={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                            }}
                                                        >
                                                            <div
                                                                {...provided.dragHandleProps}
                                                                style={{
                                                                    cursor:
                                                                        'grab',
                                                                    padding: theme.spacing(
                                                                        1,
                                                                    ),
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                    color:
                                                                        theme
                                                                            .palette
                                                                            .text
                                                                            .secondary,
                                                                }}
                                                            >
                                                                <svg
                                                                    width="24"
                                                                    height="24"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                >
                                                                    <path
                                                                        d="M9 5h2v2H9V5zm0 6h2v2H9v-2zm0 6h2v2H9v-2zm4-12h2v2h-2V5zm0 6h2v2h-2v-2zm0 6h2v2h-2v-2z"
                                                                        fill="currentColor"
                                                                    />
                                                                </svg>
                                                            </div>
                                                            <div
                                                                style={{
                                                                    flex: 1,
                                                                }}
                                                            >
                                                                <InventoryConfigRow
                                                                    inventoryConfigList={
                                                                        inventoryConfigList
                                                                    }
                                                                    inventoryConfig={
                                                                        inventoryConfig
                                                                    }
                                                                    theme={
                                                                        theme
                                                                    }
                                                                    statusGroupList={
                                                                        statusGroupList
                                                                    }
                                                                    hasEditPermission={
                                                                        hasEditPermission
                                                                    }
                                                                    onRemoveInvType={() => {
                                                                        setInvItemsMap(
                                                                            {
                                                                                ...invItemsMap,
                                                                                [inventoryConfig.id]: false,
                                                                            },
                                                                        )
                                                                        setInventoryOrder(
                                                                            inventoryOrder.filter(
                                                                                (
                                                                                    id,
                                                                                ) =>
                                                                                    id !==
                                                                                    inventoryConfig.id,
                                                                            ),
                                                                        )
                                                                    }}
                                                                    updateInventoryConfig={
                                                                        updateInventoryConfig
                                                                    }
                                                                />
                                                            </div>
                                                        </Container>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ),
                                    )}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                ) : (
                    selectedAreaInventory.map((inventoryConfig, index) => (
                        <InventoryConfigRow
                            key={`INV_CONF_${inventoryConfig.id}_${index}`}
                            inventoryConfigList={inventoryConfigList}
                            inventoryConfig={inventoryConfig}
                            theme={theme}
                            statusGroupList={statusGroupList}
                            hasEditPermission={hasEditPermission}
                            onRemoveInvType={() => {
                                setInvItemsMap({
                                    ...invItemsMap,
                                    [inventoryConfig.id]: false,
                                })
                                setInventoryOrder(
                                    inventoryOrder.filter(
                                        (id) => id !== inventoryConfig.id,
                                    ),
                                )
                            }}
                            updateInventoryConfig={updateInventoryConfig}
                        />
                    ))
                )}
            </Container>

            <Container justifyContent="flex-end">
                <Button
                    variant="outlined"
                    size="small"
                    color="secondary"
                    style={{
                        textTransform: 'none',
                        margin: theme.spacing(1, 0, 1, 2),
                    }}
                    onClick={() => setTab(CONGIFURE_INSPECTION_TYPE_STEP)}
                >
                    Back
                </Button>
                <Button
                    variant="contained"
                    style={{
                        margin: theme.spacing(1, 0, 1, 2),
                        backgroundColor: '#008C85',
                        color: 'white',
                        textTransform: 'none',
                        cursor: 'pointer',
                    }}
                    onClick={() => saveInspectionType()}
                >
                    {inspectionType ? 'Update' : 'Create'}
                </Button>
            </Container>
        </Container>
    )
}
